import { useFormikContext } from 'formik'
import { Opportunity } from '@chunker/chunker-request'
import { Box, Tooltip } from '@mui/material'
import { DeepPartial } from '@chunker/chunker-request/dist/types/DeepPartial'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import ChunkerButtonWithLoading from 'components/ChunkerButtonWithLoading'
import deepDifference from 'lib/utils/objectUtils/deepDifference'
import useNotify from 'hooks/useNotify'
import opportunitiesResource from '@chunker/chunker-request/dist/opportunitiesResource'
import extractData from 'lib/utils/requestUtils/extractData'
import ChunkerButton from 'components/ChunkerButton'
import { useOpportunityFormPaginationContext } from 'hooks/useOpportunityFormPagination'
import { Id } from '@chunker/chunker-request/dist/types/Id'
import { isObject } from 'lodash'
import { IOpportunityActionsButtonsProps } from './types'

function ButtonRow({ ...props }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: (t) => t.spacing(2),
        alignItems: 'center',
      }}
      {...props}
    />
  )
}

function OpportunityActionsButtons({
  action,
  reinitializeOpportunity,
  disableNext,
  disabledTooltip,
}: IOpportunityActionsButtonsProps) {
  const {
    initialValues,
    values,
    setSubmitting,
    isValid,
    isValidating,
    validateForm,
    isSubmitting,
  } = useFormikContext<DeepPartial<Opportunity>>()

  const { notifyError, notifySuccess } = useNotify()
  const { setCurrentStep, currentStep } = useOpportunityFormPaginationContext()
  const differenceObject = deepDifference(values, initialValues)
  const changesWereMade = Object.keys(differenceObject).length > 0
  const hasOpportunityId = Boolean(values.id)

  const steps = ['step1', 'step2', 'step3']

  const getNextStepIndex = (cs: number) => {
    const currentIndex = cs

    if (action === 'back') {
      return Math.max(currentIndex - 1, 0)
    }
    if (action === 'next') {
      return Math.min(currentIndex + 1, steps.length - 1)
    }
    return currentIndex
  }

  const nextStepIndex = getNextStepIndex(currentStep)

  const handleSaveOnly = () => {
    setSubmitting(true)
    const payload = { id: values.id, ...differenceObject }
    opportunitiesResource.requests
      .patchOpportunitiesById(values.id as Id, payload)
      .then(extractData)
      .then((data) => {
        reinitializeOpportunity(data)
        notifySuccess('Opportunity saved successfully.')
      })
      .then(async () => {
        await validateForm().catch(console.error)
      })
      .catch((err) => {
        console.error(err)
        notifyError('Failed to save opportunity.')
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleButtonClicked = () => {
    if (action === 'back') {
      if (changesWereMade && !hasOpportunityId) {
        notifyError('Opportunity ID is missing, something went wrong. Please contact support.')
        return
      }
    }

    if (action === 'next') {
      if (changesWereMade && !hasOpportunityId) {
        setSubmitting(true)
        opportunitiesResource.requests
          .postOpportunities({ ...values })
          .then(extractData)
          .then((data) => {
            reinitializeOpportunity(data)
            notifySuccess('Opportunity created successfully.')
          })
          .then(async () => {
            await validateForm().catch(console.error)
          })
          .catch((err) => {
            if (isObject(err)) {
              const { message } = err as { message: string }
              notifyError(message)
            }
            console.error(err)
          })
          .finally(() => {
            setSubmitting(false)
            setCurrentStep(nextStepIndex)
          })
      }
    }

    if (!changesWereMade && hasOpportunityId) {
      setCurrentStep(nextStepIndex)
    }

    if (changesWereMade && hasOpportunityId) {
      setSubmitting(true)
      const payload = { id: values.id, ...differenceObject }
      opportunitiesResource.requests
        .patchOpportunitiesById(values.id as Id, payload)
        .then(extractData)
        .then((data) => {
          reinitializeOpportunity(data)
          notifySuccess('Opportunity saved successfully.')
        })
        .then(async () => {
          await validateForm().catch(console.error)
        })
        .catch((err) => {
          console.error(err)
          notifyError('Failed to saved opportunity.')
        })
        .finally(() => {
          setSubmitting(false)
          setCurrentStep(nextStepIndex)
        })
    }
  }

  const renderBackButton = () => (
    <ChunkerButtonWithLoading
      type="button"
      loading={isSubmitting}
      loadingVariant="start"
      startIcon={<ArrowBack />}
      onClick={handleButtonClicked}
      disabled={!isValid || isValidating || isSubmitting}
    >
      {changesWereMade ? 'Save & Go Back' : 'Go Back'}
    </ChunkerButtonWithLoading>
  )

  const renderNextButton = () => (
    <Tooltip title={disableNext ? disabledTooltip : ''} arrow placement="top">
      {/* span will receive hover events */}
      <span style={{ display: 'inline-block' }}>
        <ChunkerButtonWithLoading
          type="button"
          loading={isSubmitting}
          loadingVariant="end"
          endIcon={<ArrowForward />}
          onClick={handleButtonClicked}
          disabled={!isValid || isValidating || isSubmitting || Boolean(disableNext)}
        >
          {changesWereMade ? 'Save & Continue' : 'Continue'}
        </ChunkerButtonWithLoading>
      </span>
    </Tooltip>
  )

  const renderSaveOnlyButton = () => (
    <ChunkerButton
      type="button"
      onClick={handleSaveOnly}
      disabled={!isValid || isValidating || isSubmitting || !changesWereMade}
    >
      {changesWereMade ? 'Save Progress' : 'Progress Saved'}
    </ChunkerButton>
  )

  return (
    <ButtonRow>
      <>
        {action === 'back' && renderBackButton()}
        {action === 'save' && renderSaveOnlyButton()}
        {action === 'next' && renderNextButton()}
      </>
    </ButtonRow>
  )
}

export default OpportunityActionsButtons
